import { useEffect, useRef, useState, useCallback } from 'react';
import { logger } from '../services/logger.ts';

export interface InactivityTimeoutConfig {
  inactivityTimeout: number; // milliseconds
  warningTime: number; // milliseconds before timeout to show warning
  checkInterval?: number; // how often to check for inactivity
}

export interface InactivityTimeoutState {
  isWarningVisible: boolean;
  timeRemaining: number;
  resetTimer: () => void;
  extendSession: () => void;
}

/**
 * Hook to track user inactivity and trigger timeouts
 * Monitors mouse, keyboard, touch, and scroll events
 */
export function useInactivityTimeout(
  config: InactivityTimeoutConfig,
  onTimeout: () => void,
  enabled: boolean = true
): InactivityTimeoutState {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const checkTimerRef = useRef<number | null>(null);
  const isTimedOutRef = useRef(false);
  const isPageActiveRef = useRef(true);

  const {
    inactivityTimeout,
    warningTime,
    checkInterval = 1000, // Check every second by default
  } = config;

  // Reset the activity timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsWarningVisible(false);
    isTimedOutRef.current = false;
    
    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    logger.info('User activity detected, timer reset');
  }, []);

  // Extend session when user clicks "Stay Logged In"
  const extendSession = useCallback(() => {
    resetTimer();
    logger.info('Session extended by user');
  }, [resetTimer]);

  // Handle user activity events
  const handleActivity = useCallback(() => {
    if (!enabled || isTimedOutRef.current) return;
    resetTimer();
  }, [enabled, resetTimer]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    if (!enabled || isTimedOutRef.current) return;
    // Avoid timing out when the app tab/window is not active (e.g. DevTools focus).
    if (!isPageActiveRef.current || document.hidden) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    const timeUntilTimeout = inactivityTimeout - timeSinceLastActivity;

    // If timeout reached
    if (timeSinceLastActivity >= inactivityTimeout) {
      logger.warn('Session timeout due to inactivity', {
        inactivityDuration: timeSinceLastActivity,
        configuredTimeout: inactivityTimeout,
      });
      
      isTimedOutRef.current = true;
      setIsWarningVisible(false);
      
      // Clear all timers
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
      
      onTimeout();
      return;
    }

    // If warning period reached but not yet timed out
    if (timeUntilTimeout <= warningTime && !isWarningVisible) {
      logger.info('Showing inactivity warning', {
        timeRemaining: timeUntilTimeout,
      });
      
      setIsWarningVisible(true);
      setTimeRemaining(Math.ceil(timeUntilTimeout / 1000));

      // Start countdown timer
      countdownTimerRef.current = window.setInterval(() => {
        const currentTimeRemaining = inactivityTimeout - (Date.now() - lastActivityRef.current);
        
        if (currentTimeRemaining <= 0) {
          setTimeRemaining(0);
        } else {
          setTimeRemaining(Math.ceil(currentTimeRemaining / 1000));
        }
      }, 1000);
    }
  }, [enabled, inactivityTimeout, warningTime, isWarningVisible, onTimeout]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      setIsWarningVisible(false);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity detection to avoid excessive calls
    let throttleTimeout: number | null = null;
    const throttledHandleActivity = () => {
      if (!throttleTimeout) {
        handleActivity();
        throttleTimeout = window.setTimeout(() => {
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledHandleActivity, true);
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPageActiveRef.current = false;
        return;
      }

      isPageActiveRef.current = true;
      resetTimer();
    };

    const handleWindowBlur = () => {
      isPageActiveRef.current = false;
    };

    const handleWindowFocus = () => {
      isPageActiveRef.current = true;
      resetTimer();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Start checking for inactivity
    checkTimerRef.current = window.setInterval(checkInactivity, checkInterval);

    // Initial activity
    resetTimer();

    return () => {
      // Clean up event listeners
      events.forEach((event) => {
        window.removeEventListener(event, throttledHandleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);

      // Clear timers
      if (throttleTimeout) clearTimeout(throttleTimeout);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    };
  }, [enabled, handleActivity, checkInactivity, checkInterval, resetTimer]);

  return {
    isWarningVisible,
    timeRemaining,
    resetTimer,
    extendSession,
  };
}

